import {booleanAttribute, ChangeDetectionStrategy, Component, effect, input} from "@angular/core";
import {MuterendeOppgaveSpesifikasjon, OppgaveModell, Oppgavestatus} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {alleEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatError, MatFormField, MatLabel, MatPrefix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {TranslateModule} from "@ngx-translate/core";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect, MatSelectTrigger} from "@angular/material/select";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {switchMap} from "rxjs";
import {aktiverFormKontroller} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {skrivZonedDateTimeSerialisert, zonedDateTimeSluttPåDagenFraPlainDate} from "felles-ng2/støttefunksjoner/temporal";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {oppgaveUtførtdatoSomPlainDate} from "felles-ng2/modell/oppgaveModellHjelpere";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('OppgaveStatusSkjemaKomponent');

export type SkjemaType = ReturnType<typeof OppgaveStatusSkjemaKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];
export type OppgaveStatusSpesifikasjon = Pick<MuterendeOppgaveSpesifikasjon, 'status' | 'utfoertDato'>;

/**
 * GUI for å endre oppgavestatus
 */
@Component({
    selector: 'oppgave-status-skjema',
    templateUrl: 'oppgave-status-skjema.html',
    styleUrl: 'oppgave-status-skjema.scss',
    host: {'[class]': '"status_"+skjema().value.status'},
    imports: [
        MatDatepicker,
        MatDatepickerToggle,
        MatError,
        MatFormField,
        MatInput,
        MatLabel,
        MatPrefix,
        ReactiveFormsModule,
        StandardValideringsfeilPipe,
        TranslateModule,
        MatOption,
        MatSelect,
        MatSelectTrigger,
        MatDatepickerInput
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OppgaveStatusSkjemaKomponent {

    static opprettSkjema(status: Oppgavestatus = Oppgavestatus.ikke_påbegynt, utfoertDato: PlainDate | null = null) {
        return new FormGroup({
            status: new FormControl<Oppgavestatus>(status, {nonNullable: true}),
            utfoertDato: new FormControl<PlainDate | null>(utfoertDato),
        });
    }

    static populerSkjema(skjema: SkjemaType, oppgave: OppgaveModell, tidssone: Tidssoneinformasjon): void {
        skjema.patchValue({
            ...oppgave,
            utfoertDato: oppgaveUtførtdatoSomPlainDate(oppgave, tidssone)
        });
    }

    static somSpesifikasjon(skjema: SkjemaType, tidssone: Tidssoneinformasjon): OppgaveStatusSpesifikasjon {
        return {
            status: skjema.value.status!,
            utfoertDato: skjema.value.status == Oppgavestatus.utført ?
              skrivZonedDateTimeSerialisert(zonedDateTimeSluttPåDagenFraPlainDate(skjema.value.utfoertDato!, tidssone)) :
              null
        }
    }

    skjema = input.required<SkjemaType>();
    autolagre = input(false, {transform: booleanAttribute});

    initiellUtførtdato: PlainDate | null = null;
    statuser = alleEnuminnslag<Oppgavestatus>(Oppgavestatus);
    statuserNavn = this.enumOversettelser.oppgaveStatusOversettelser;
    utfoertEnumVerdi = Oppgavestatus.utført;

    constructor(private enumOversettelser: EnumOversettelser, private klokke: Klokke) {
        this.aktiverUtførtDatofeltDersomStatusUtført();
    }

    erValgt(a: StatusModell, b: StatusModell): boolean {
        return a.status === b.status;
    }

    //private hjelpemetoder

    private aktiverUtførtDatofeltDersomStatusUtført() {
        effect(() => {
            this.initiellUtførtdato = this.skjema().value.utfoertDato ?? null;
            this.oppdaterUtførtDatoAktivertStatus(this.skjema().value.status!);
        });

        toObservable(this.skjema)
          .pipe(
            switchMap(skjema => skjema.controls.status.valueChanges),
            takeUntilDestroyed())
          .subscribe(nyStatus => {
              debug(`status endret til ${Oppgavestatus[nyStatus]}`)
              this.oppdaterUtførtDatoAktivertStatus(nyStatus);
              if (nyStatus != Oppgavestatus.utført) return;
              if (erNil(this.skjema().value.utfoertDato))
                  this.skjema().controls.utfoertDato.setValue(this.klokke.nåSomPlainDate());
          });
    }

    private oppdaterUtførtDatoAktivertStatus(nyStatus: Oppgavestatus) {
        aktiverFormKontroller(nyStatus == Oppgavestatus.utført, this.skjema().controls.utfoertDato);
    }
}

interface StatusModell {
    navn: string;
    status: Oppgavestatus;
}
